import getValue from 'get-value';
import setValue from 'set-value';
import bugAlert from './bugAlert';

export const alphaRegex = /^[a-zA-Z]/;
export const insertRegex = /^(<|>) *#?(\S+)/;

/**
 * Searches an object recursively for a nested object with the given value as its `id` prop.
 *
 * Returns the dot-prop path to that nested object.
 *
 * `path` stores the latest path during recursion.
 */
export const findDotPathToId = (
  obj: object,
  id: string,
  path: string[] = []
): string | undefined => {
  if (!alphaRegex.test(id[0])) return undefined;

  for (const [k, v] of Object.entries(obj)) {
    path.push(k);
    let found: string | undefined;

    if (v && typeof v === 'object') {
      // we found it! return the path that got us here
      if (v.id === id) return path.join('.');
      // keep searching
      else found = findDotPathToId(v, id, path);
    }

    // if we found it, pass the result up the stack
    if (found) return found;

    // otherwise, remove the last path segment and keep searching
    path.pop();
  }

  return undefined;
};

export const findDotPathToIdOrThrow = (obj: object, id: string): string => {
  const path = findDotPathToId(obj, id);
  if (!path)
    throw new Error(
      `Could not find id "${id}" in the course while applying a patch.`
    );

  return path;
};

export const findScreenById = (
  obj: Content,
  id: string
): UcScreenRequiredId | null => {
  if (!obj?.pages) return null;

  for (const page of obj.pages) {
    for (const s of page.screens) {
      if (s.id === id) return s;
    }
  }

  return null;
};

export const applyPatch =
  (content: Content) =>
  /**
   * Applies a patch to the content object.
   *
   * The behavior depends on the key of the patch:
   *
   * 1.
   * "$path.to.key" replaces one value by dot path
   *
   * @exmaple
   * ```yml
   * '$course.logo': { value: '/path/to/custom-logo.svg' }
   * ```
   *
   * 2.
   * "#id.path.to.key" replaces one value by dot path, starting from the page or screen with given id
   *
   * @exmaple
   * ```yml
   * '#my_page_id.title': { value: 'New page title' }
   * ```
   *
   * 3.
   * "< #id" inserts a new page or screen _before_ the given id. The "#" and whitespace are optional.
   *
   * @exmaple
   * ```yml
   * '< #unit_2':
   *   - id: unit_1a
   *     title: Before unit 2
   * ```
   *
   * 4.
   * "> #id" inserts a new page or screen _after_ the given id. The "#" and whitespace are optional.
   *
   * @exmaple
   * ```yml
   * '> #unit_2':
   *   - id: unit_2a
   *     title: After unit 2
   * ```
   *
   * 5.
   *
   * Any other key is assumed to be a screen id, and the patch replaces the entire screen.
   *
   * @exmaple
   * ```yml
   * my_screen_id:
   *  - id: my_screen_id
   *    title: New screen title
   * ```
   *
   * @param param0 An object entry pair of a key and a patch
   */
  ([k, [patch]]: [string, Patch]) => {
    try {
      if (k.startsWith('$') && 'value' in patch) {
        setValue(content, k.slice(1), patch.value);

        return true;
      }

      if (k.startsWith('#') && 'value' in patch) {
        const [id, ...path] = k.slice(1).split('.');
        const pathToId = findDotPathToIdOrThrow(content, id);
        setValue(content, [pathToId, ...path].join('.'), patch.value);

        return true;
      }

      // Attempt to apply and advanced patch (adding a page before or after another ID)
      // Return false if it fails so we can reattempt. The target ID may not have been added yet.
      if (insertRegex.test(k)) {
        try {
          const [, direction, id] = k.match(insertRegex) || [];
          const pathToId = findDotPathToIdOrThrow(content, id);

          // adv patches can only be inserted before or after a page/screen ID
          if (!pathToId.startsWith('pages')) {
            throw new Error(
              `Advance patch "${k}" must target a page or screen ID.`
            );
          }

          const pathToParent = pathToId.split('.').slice(0, -1).join('.');
          const parentObj = getValue(content, pathToParent);
          const index = parentObj.findIndex((c: any) => c.id === id);
          const splice = index + (direction === '<' ? 0 : 1);
          parentObj.splice(splice, 0, patch);

          return true;
        } catch (e) {
          return false;
        }
      }

      // normal patch (replace the whole base screen)
      const pathToScreen = findDotPathToIdOrThrow(content.pages, k);
      const pathToParent = pathToScreen.split('.').slice(0, -1).join('.');
      const parentObj = getValue(content.pages, pathToParent);
      const index = parentObj.findIndex((c: any) => c.id === k);
      parentObj.splice(index, 1, patch);

      return true;
    } catch (e) {
      bugAlert(`Could not apply patch "${k}"`);
    }
  };

export default applyPatch;
